// src/hooks/usePropertyImages.js
import { useState, useCallback } from 'react';
import { apiFetch } from '../utils/apiClient';
import { BASE_URL, getFullImageUrl } from '../utils/config';

const usePropertyImages = (initialFormData, setFormData, addNotification, user) => {
  const [stagedImages, setStagedImages] = useState([]);
  const [uploadingFilesList, setUploadingFilesList] = useState([]);

  // Handler for staging an image before the property is saved
  const handleStageImage = useCallback(
    (file, isMain, tempId) => {
      console.log(`usePropertyImages: Staging image. File: ${file?.name}, isMain: ${isMain}`);
      if (!file) {
        addNotification('No file selected for upload.', 'error');
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // reader.result contains the data: URL which is allowed by the CSP
        const localUrl = reader.result;

        setUploadingFilesList((prev) => [...prev, { tempId, fileName: file.name, isMain, status: 'uploading', progress: 0 }]);

        const uploadFormData = new FormData();
        uploadFormData.append('image', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BASE_URL}/api/properties/images/stage`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${user.token}`);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadingFilesList((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, progress: percentComplete } : f)));
          }
        };

        xhr.onload = () => {
          setUploadingFilesList((prev) => prev.filter((f) => f.tempId !== tempId));
          try {
            const responseData = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              const newStagedImage = {
                localUrl: localUrl, // Use the data: URL
                tempId: responseData.tempId,
                isMain: false,
              };

              setStagedImages((prev) => {
                let newImages = [...prev, newStagedImage];
                if (isMain || newImages.length === 1) {
                  newImages = newImages.map((img, index) => ({ ...img, isMain: index === newImages.length - 1 }));
                }
                return newImages;
              });
              addNotification('Image staged successfully!', 'success');
            } else {
              throw new Error(responseData.message || 'Image staging failed.');
            }
          } catch (err) {
            addNotification(err.message, 'error');
          }
        };
        xhr.send(uploadFormData);
      };
      reader.onerror = () => {
        addNotification('Could not create image preview.', 'error');
      };
    },
    [addNotification, user.token]
  );

  // Handler for uploading an image to an already saved property
  const handleDirectImageUpload = useCallback(
    (file, isMain, tempId, propertyID) => {
      console.log(`usePropertyImages: Direct upload. File: ${file?.name}, isMain: ${isMain}`);
      setUploadingFilesList((prev) => [...prev, { tempId, fileName: file.name, isMain, status: 'uploading', progress: 0 }]);

      const uploadFormData = new FormData();
      uploadFormData.append('propertyID', propertyID);
      uploadFormData.append('isMain', isMain);
      uploadFormData.append('propertyImage', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE_URL}/api/properties/upload-image`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${user.token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          const newStatus = percentComplete === 100 ? 'processing' : 'uploading';
          setUploadingFilesList((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, progress: percentComplete, status: newStatus } : f)));
        }
      };

      xhr.onload = () => {
        setUploadingFilesList((prev) => prev.filter((f) => f.tempId !== tempId));
        try {
          const responseData = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            setFormData((prevFormData) => ({ ...prevFormData, ...responseData.property }));
            addNotification('Image uploaded successfully!', 'success');
          } else {
            throw new Error(responseData.error || `Image upload failed: ${xhr.statusText}`);
          }
        } catch (err) {
          addNotification(err.message || 'An unexpected error occurred during image upload.', 'error');
        }
      };
      xhr.send(uploadFormData);
    },
    [addNotification, user.token, setFormData]
  );

  // Main upload handler that decides whether to stage or upload directly
  const handleImageUpload = useCallback(
    (file, isMain, tempId) => {
      const propertyID = initialFormData?.propertyID;
      if (propertyID) {
        handleDirectImageUpload(file, isMain, tempId, propertyID);
      } else {
        handleStageImage(file, isMain, tempId);
      }
    },
    [initialFormData, handleDirectImageUpload, handleStageImage]
  );

  // Handler for deleting a saved image
  const handleImageDelete = useCallback(
    async (imageUrlToDelete, isMain) => {
      const propertyID = initialFormData?.propertyID;
      if (!propertyID) return;
      try {
        const response = await apiFetch(`${BASE_URL}/api/properties/delete-image`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ propertyID, imageUrl: imageUrlToDelete }) }, user.token);
        const responseData = await response.json();
        if (!response.ok) throw new Error(responseData.error || 'Image delete failed.');
        setFormData((prev) => ({ ...prev, ...responseData.property }));
        addNotification('Image deleted successfully!', 'success');
      } catch (err) {
        addNotification(err.message, 'error');
      }
    },
    [initialFormData, addNotification, user.token, setFormData]
  );

  // Handler for deleting a staged image
  const handleDeleteStagedImage = useCallback((tempIdToDelete) => {
    setStagedImages((prev) => {
      const imageToDelete = prev.find((img) => img.tempId === tempIdToDelete);
      const remainingImages = prev.filter((img) => img.tempId !== tempIdToDelete);
      if (imageToDelete?.isMain && remainingImages.length > 0) {
        remainingImages[0].isMain = true;
      }
      return remainingImages;
    });
  }, []);

  // Handler for setting a staged image as main
  const handleSetMainStagedImage = useCallback((tempIdToSetAsMain) => {
    setStagedImages((prev) => prev.map((img) => ({ ...img, isMain: img.tempId === tempIdToSetAsMain })));
  }, []);

  // Handler for setting a saved image as main
  const handleSetMainImage = useCallback(
    async (newMainImageUrl) => {
      const propertyID = initialFormData?.propertyID;
      if (!propertyID) return;
      try {
        const response = await apiFetch(`${BASE_URL}/api/properties/set-main-image/${propertyID}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ imageUrl: newMainImageUrl }) }, user.token);
        const responseData = await response.json();
        if (!response.ok) throw new Error(responseData.message || 'Failed to set main image.');
        setFormData((prev) => ({ ...prev, mainImageURL: responseData.mainImageURL, propertyImages: responseData.propertyImages }));
        addNotification('Main image updated successfully!', 'success');
      } catch (err) {
        addNotification(err.message, 'error');
      }
    },
    [initialFormData, addNotification, user.token, setFormData]
  );

  // Handler for rotating a saved image
  const handleImageRotate = useCallback(
    async (image, direction) => {
      if (image.type === 'staged') {
        // Logic for rotating a new, staged image
        try {
          const response = await apiFetch(
            `${BASE_URL}/api/properties/staged-image/rotate`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tempId: image.id, direction }),
            },
            user.token
          );
          const responseData = await response.json();
          if (!response.ok) throw new Error(responseData.error || 'Staged image rotate failed.');

          // The backend returns a new relative URL. We need to update the preview URL in our state.
          // We use getFullImageUrl to create an absolute URL that the browser can display.
          const newFullUrl = getFullImageUrl(responseData.newImageUrl);
          setStagedImages((prev) => prev.map((img) => (img.tempId === image.id ? { ...img, localUrl: newFullUrl } : img)));
          addNotification('Image rotated successfully!', 'success');
        } catch (err) {
          addNotification(err.message, 'error');
        }
      } else {
        // Existing logic for rotating a saved image
        const imageUrl = image.url;
        const propertyID = initialFormData?.propertyID;
        if (!propertyID) return;
        try {
          const response = await apiFetch(`${BASE_URL}/api/properties/rotate-image`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ propertyID, imageUrl, direction }) }, user.token);
          const responseData = await response.json();
          if (!response.ok) throw new Error(responseData.error || 'Image rotate failed.');
          setFormData((prev) => {
            const isMain = prev.mainImageURL === imageUrl;
            const newPropertyImages = prev.propertyImages.map((url) => (url === imageUrl ? responseData.newImageUrl : url));
            return { ...prev, mainImageURL: isMain ? responseData.newImageUrl : prev.mainImageURL, propertyImages: newPropertyImages };
          });
          addNotification('Image rotated successfully!', 'success');
        } catch (err) {
          addNotification(err.message, 'error');
        }
      }
    },
    [initialFormData, addNotification, user.token, setFormData]
  );

  const clearStagedImages = useCallback(() => {
    setStagedImages([]);
  }, []);

  return { stagedImages, uploadingFilesList, handleImageUpload, handleImageDelete, handleDeleteStagedImage, handleSetMainStagedImage, handleSetMainImage, handleImageRotate, clearStagedImages };
};

export default usePropertyImages;
